import { Button, Col, Row } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import EditableCell from "containers/common/EditableCell";
import { GenericTable } from "components/GenericTable";
import { Link } from "react-router-dom";
import { PacmanLoader } from "react-spinners";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";

function SaleBuyerSellerList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  saveRecord,
  deleteRecord,
  refreshDataSet,
  updateTableState,
  filter,
  user,
  buyerSellerOptions,
}) {
  const dataSet = "buyerSellers";
  const tableData = Array.isArray(data) ? data : [];
  const columnHelper = createColumnHelper();

  const buyerVendorOptions = (buyerSellerOptions || [
    { value: true, label: "Vendor" },
    { value: false, label: "Buyer" },
  ])
    .filter((o) => String(o?.label || "").trim().toLowerCase() !== "select...")
    .map((o) => ({
      value: String(o.value),
      label: o.label,
    }));

  function updateRowData(rowIndex, columnId, value) {
    const localData = [...tableData];
    const updated = { ...localData[rowIndex], [columnId]: value };

    if (columnId === "buyer_vendor") {
      const asString = String(value).toLowerCase();
      updated[columnId] = asString === "true" || asString === "vendor";
    }

    localData[rowIndex] = updated;
    updateTableState(localData, dataSet);
  }

  async function handleSave({ row, column, value }) {
    const record = { ...row.original };
    record[column.id] = value;

    if (column.id === "buyer_vendor") {
      const asString = String(value).toLowerCase();
      record[column.id] = asString === "true" || asString === "vendor";
    }

    record.last_edited_by = user;

    try {
      const result = await saveRecord(record, dataSet, "buyer_seller_id");
      return !(result && result.result === false);
    } catch (err) {
      console.error("Save failed:", err);
      return false;
    }
  }

  async function handleDelete({ row }) {
    const confirmed = window.confirm("Are you sure you want to delete this record?");
    if (!confirmed) {
      return false;
    }

    const record = { ...row.original, last_edited_by: user };
    const isTempRecord = Boolean(record.new_record || String(record.buyer_seller_id || "").startsWith("temp-"));

    if (isTempRecord) {
      const updatedRows = tableData.filter((_, index) => index !== row.index);
      updateTableState(updatedRows, dataSet);
      return true;
    }

    try {
      const result = await saveRecord(
        {
          ...record,
          archived: true,
          archived_date: new Date().toISOString(),
        },
        dataSet,
        "buyer_seller_id"
      );

      if (result && result.result === false) {
        alert(result.error_message || "Failed to delete buyer/seller record.");
        return false;
      }

      const updatedRows = tableData.filter((_, index) => index !== row.index);
      updateTableState(updatedRows, dataSet);

      if (typeof refreshDataSet === "function") {
        await refreshDataSet(dataSet);
      }

      return true;
    } catch (error) {
      alert(error?.message || "Failed to delete buyer/seller record.");
      return false;
    }
  }

  const columns = [
    columnHelper.accessor("buyer_seller_id", {
      id: "buyer_seller_id",
      header: "Buyer Seller ID",
      size: 80,
    }),
    columnHelper.accessor("owner_id", {
      id: "owner_id",
      header: "Owner ID",
      cell: (e) => (
        <Link to={`../owners/${e.getValue()}`}>
          {e.getValue()}
        </Link>
      ),
      size: 80,
    }),
    columnHelper.accessor("owner_name", {
      id: "owner_name",
      header: "Owner Name",
      cell: EditableCell,
      size: 320,
    }),
    columnHelper.accessor("buyer_vendor", {
      id: "buyer_vendor",
      header: "Buyer/Vendor",
      cell: EditableCell,
      meta: { options: buyerVendorOptions, noBlankOption: true },
      size: 130,
    }),
    columnHelper.accessor("share", {
      id: "share",
      header: "Share",
      cell: EditableCell,
      meta: { suffix: "%" },
      size: 90,
    }),
    columnHelper.accessor("buyer_seller_id", {
      id: "actions",
      header: "",
      cell: (e) => (
        <Button type="button" size="sm" variant="danger" onClick={() => e.table.options.meta.handleDelete({ row: e.row })}>
          Delete
        </Button>
      ),
      size: 90,
      enableSorting: false,
    }),
  ];

  // --- Render Functions ---
  function renderComponent() {
    return (
      <div className="componentRendering">
        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading ? (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color={"#e03c31"}
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : null}
            {!isLoading && (
              <GenericTable
                columns={columns}
                data={tableData}
                section={`sale-buyer-sellers-${controlType || "table"}`}
                pageSize={PAGE_SIZES.holdChecksSaleOwnersSection}
                enablePagination={true}
                enforceMinRowsHeight={true}
                minVisibleRows={5}
                meta={{
                  updateData: updateRowData,
                  handleSave,
                  handleDelete,
                }}
              />
            )}
          </Col>
        </Row>
      </div>
    );
  }
  return (
    <div className="SaleList">
      {isAuthenticated ? renderComponent() : null}
    </div>
  );
}

export default SaleBuyerSellerList;